import { useEffect, useState } from 'react';
import { FormikErrors } from 'formik';
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { Logger } from '@utils/logger';
import { SelectOption } from '@iddataweb/idw-common-ui/types';
import { AttributeTypeField } from '@idw-types/data';
import styles from "./custom-radio-button-field.less";
import { AttributeStatusTypes } from '@enums/attribute-status-types';
import { AXNUIRadioGroupInput } from '@iddataweb/idw-common-ui/containers';

type CustomRadioButtonFieldProps = {
    attributeFieldOptions: Array<SelectOption>;
    attribute: AttributeFormState;
    attributeTypeField: AttributeTypeField;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    onSetSubmitValue: (data: { inputData: { [key: string]: string } }) => void;
    isSubmitting: boolean;
};

export const CustomRadioButtonField = (props: CustomRadioButtonFieldProps) => {
    const {
        attributeFieldOptions,
        attribute,
        attributeTypeField,
        setFieldValue,
        setFieldTouched,
        onSetSubmitValue,
        isSubmitting
    } = props;

    const [hasPerformedForcedUpdate, setHasPerformedForcedUpdate] = useState(false);

    const setFieldValueInternal = (newValue: string) => {
        setFieldValue(attributeTypeField.name, newValue);
        const inputData = { ...attribute.inputData, [attributeTypeField.name]: newValue };
        onSetSubmitValue({ inputData });
    };

    const initializeCustomRadioButtonInput = async (value: string) => {
        const inputData = { ...attribute.inputData, [attributeTypeField.name]: value };
        Logger.info('[CustomRadioButton] submit value set:', inputData);
        await setFieldValue(attributeTypeField.name, value);
        onSetSubmitValue({ inputData });
        setFieldTouched(attributeTypeField.name, true, true);
    };

    useEffect(() => {
        if (
            attribute.isPlaceholder &&
            attribute.prefillValueMap[attributeTypeField.name] &&
            !attribute.inputData[attributeTypeField.name]
        ) {
            const selectedOption = attributeFieldOptions.find(afo => afo.value === attribute.prefillValueMap[attributeTypeField.name]);
            if (selectedOption) {
                initializeCustomRadioButtonInput(selectedOption.value)
            }
        } else if (!attribute.inputData[attributeTypeField.name] && attribute.status === AttributeStatusTypes.NEW) {
            initializeCustomRadioButtonInput(attributeFieldOptions[0].value);
        }
    }, [
        attribute.isPlaceholder,
        attribute.prefillValueMap[attributeTypeField.name],
        attribute.inputData[attributeTypeField.name],
        attribute.status
    ]);

    useEffect(() => {
        if (!hasPerformedForcedUpdate && attribute.inputData[attributeTypeField.name]) {
            setHasPerformedForcedUpdate(true);
            setFieldValueInternal(attribute.inputData[attributeTypeField.name]);
        }
    }, [hasPerformedForcedUpdate, attribute.inputData[attributeTypeField.name]]);

    return (
        <div className={styles.customRadioButtonField}>
            <AXNUIRadioGroupInput
                value={attribute.inputData[attributeTypeField.name]}
                idPrefix={`attr${attribute.id}-${attributeTypeField.name}`}
                preferences={attributeFieldOptions}
                onChange={setFieldValueInternal}
                isSubmitting={isSubmitting}
                isPlaceholder={attribute.isPlaceholder}
                isDisabled={attribute.isDisabled}
            />
        </div>
    );
};